import { useEffect, useState } from "react";
import { Loader2, Save, Shield, FileText } from "lucide-react";
import { toast } from "sonner";
import type { PanelLevel } from "../config/panelCatalog";
import type { PanelPermission } from "../api/access";
import { createAccess, type AccessProfile } from "../api/access";
import { createRole, type Role } from "../api/roles";
import { blankPermissions } from "../utils/accessPermissions";
import { PermissionGridEditor } from "./PermissionGridEditor";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

/**
 * In-form quick-create dialog for a Role or an Access profile. Built on the
 * shared Radix Dialog so it nests correctly over the (also Radix) Add-Employee
 * modal — the inner dialog becomes the top layer with its own focus + pointer
 * scope (a plain body-portaled overlay would be made inert by the parent).
 *
 * Reuses the same panel-permission grid as Settings, so the new role/access is
 * fully configured before it's selected. On success it hands the created record
 * back via `onCreated` so the caller can append + select it.
 */
type Props =
  | { mode: "role"; open: boolean; onClose: () => void; onCreated: (r: Role) => void }
  | { mode: "access"; open: boolean; onClose: () => void; onCreated: (a: AccessProfile) => void };

export function RoleAccessQuickCreate(props: Props) {
  const { mode, open, onClose } = props;
  const [name, setName] = useState("");
  const [rank, setRank] = useState(100);
  const [description, setDescription] = useState("");
  const [permissions, setPermissions] = useState<Record<string, PanelPermission>>(() =>
    blankPermissions()
  );
  const [saving, setSaving] = useState(false);

  // Reset to a clean form each time the dialog opens.
  useEffect(() => {
    if (open) {
      setName("");
      setRank(100);
      setDescription("");
      setPermissions(blankPermissions());
    }
  }, [open]);

  const setVisible = (key: string, visible: boolean) =>
    setPermissions((p) => ({ ...p, [key]: { ...p[key], visible } }));
  const setLevel = (key: string, level: PanelLevel) =>
    setPermissions((p) => ({ ...p, [key]: { ...p[key], level } }));

  const submit = async () => {
    if (!name.trim()) {
      toast.error(mode === "role" ? "Give this role a name" : "Give this access a name");
      return;
    }
    setSaving(true);
    try {
      if (props.mode === "role") {
        const created = await createRole({ name: name.trim(), rank, permissions });
        toast.success("Role created");
        props.onCreated(created);
      } else {
        const created = await createAccess({ name: name.trim(), description, permissions });
        toast.success("Access created");
        props.onCreated(created);
      }
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  const Icon = mode === "role" ? Shield : FileText;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="w-5 h-5 text-emerald-600" />
            {mode === "role" ? "New Role" : "New Access"}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
              {mode === "role" ? "Role Name" : "Access Name"}
            </label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={mode === "role" ? "e.g. Auditor" : "e.g. Read-only"}
              className="mt-1.5 w-full h-11 px-3.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-400"
            />
          </div>
          {mode === "role" ? (
            <div>
              <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                Rank (lower = higher authority)
              </label>
              <input
                type="number"
                value={rank}
                onChange={(e) => setRank(Number(e.target.value))}
                className="mt-1.5 w-full h-11 px-3.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-400"
              />
            </div>
          ) : (
            <div>
              <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                Description (optional)
              </label>
              <input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What this access is for"
                className="mt-1.5 w-full h-11 px-3.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-400"
              />
            </div>
          )}
        </div>

        <PermissionGridEditor permissions={permissions} setVisible={setVisible} setLevel={setLevel} />

        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-medium text-gray-600 px-4 h-10 rounded-xl hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-5 h-10 rounded-xl disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {mode === "role" ? "Create Role" : "Create Access"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
